import type { ContractTermsResponse } from "@bw/shared/types/generated";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { formatRateFraction } from "@bw/shared/types";
import { formatMoney } from "@bw/shared/utils";

export interface OwnerFeeTermsProps {
  terms: Pick<ContractTermsResponse, "service_fee_terms">;
}

/** Exact version-owned rules only; unavailable is never zero/current substitution. */
export function OwnerFeeTerms({ terms }: OwnerFeeTermsProps) {
  return (
    <Stack spacing={2}>
      {(terms.service_fee_terms ?? []).map((term) => (
        <Stack spacing={0.5} key={term.binding.fee_policy_version_id}>
          <Typography variant="subtitle2">
            {term.binding.selector.service_key === "classes"
              ? "Classes"
              : "Service fee terms"}{" "}
            —{" "}
            {term.binding.selector.origin === "player"
              ? "Player enrollment"
              : "Service origin"}
          </Typography>
          {term.readiness === "unavailable" ? (
            <Alert severity="warning">
              Exact fee terms unavailable. Ask the platform to review this
              edition.
            </Alert>
          ) : (
            <>
              {term.edition.rules.map((rule, index) => (
                <Stack key={index} spacing={0.25}>
                  <Typography variant="body2">
                    {rule.component === "bw_commission"
                      ? "BW commission"
                      : "Acquisition"}
                    :{" "}
                    {formatRateFraction(rule.percentage, 2).replace(
                      /\.?0+%$/,
                      "%",
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Flat fee:{" "}
                    {rule.flat_fee == null
                      ? "Not set"
                      : formatMoney(
                          rule.flat_fee.amount_minor / 100,
                          rule.flat_fee.currency,
                        )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cap:{" "}
                    {rule.cap == null
                      ? "Not set"
                      : formatMoney(
                          rule.cap.amount_minor / 100,
                          rule.cap.currency,
                        )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rounding: half up. Stacking:{" "}
                    {rule.campaign_stacking === "exclusive"
                      ? "Exclusive"
                      : "Added after commission"}
                    .
                  </Typography>
                </Stack>
              ))}
              <Typography variant="body2" color="text.secondary">
                Effective{" "}
                {new Date(term.edition.effective_from).toLocaleString()}
                {term.edition.effective_until
                  ? ` until ${new Date(term.edition.effective_until).toLocaleString()}`
                  : ""}
                .
              </Typography>
            </>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
